'use client';

import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Minus, Plus, Wine, Check } from 'lucide-react';
import { useRouter } from '@/i18n/navigation';
import {
  useGiftVoucherStore,
  PACKAGE_SPARKLING_WINE_PRICE_RON,
  PACKAGE_RESTAURANT_MIN_RON,
  PACKAGE_SPA_SERVICES,
} from '@/stores/gift-voucher-store';
import type { VoucherRoomTypeOption } from '@/lib/gift-voucher-pricing';
import { MobileStickyTotal } from './mobile-sticky-total';

interface PackageVoucherFormProps {
  roomTypes: VoucherRoomTypeOption[];
}

export function PackageVoucherForm({ roomTypes }: PackageVoucherFormProps) {
  const t = useTranslations('giftVouchers');
  const router = useRouter();
  const setDraft = useGiftVoucherStore((s) => s.setDraft);

  const [roomTypeId, setRoomTypeId] = useState<string>(roomTypes[0]?.id || '');
  const [nights, setNights] = useState(1);
  const [withSpa, setWithSpa] = useState(false);
  const [sparklingWine, setSparklingWine] = useState(false);
  const [sparklingNote, setSparklingNote] = useState('');
  const [restaurantCreditOn, setRestaurantCreditOn] = useState(false);
  // Raw string input — see value-voucher-form: the min is enforced on blur,
  // never on keystroke, so the field stays freely editable/clearable.
  const [restaurantCreditInput, setRestaurantCreditInput] = useState('');
  const [serviceCounts, setServiceCounts] = useState<Record<string, number>>({});

  const parsedCredit = parseInt(restaurantCreditInput, 10);
  const restaurantCredit = restaurantCreditOn && !Number.isNaN(parsedCredit) ? parsedCredit : 0;
  const restaurantCreditBelowMin = restaurantCreditOn && restaurantCredit > 0 && restaurantCredit < PACKAGE_RESTAURANT_MIN_RON;

  const toggleRestaurantCredit = () => {
    setRestaurantCreditOn((on) => {
      const next = !on;
      setRestaurantCreditInput(next ? String(PACKAGE_RESTAURANT_MIN_RON) : '');
      return next;
    });
  };

  const clampRestaurantCredit = () => {
    const clamped = Number.isNaN(parsedCredit) || parsedCredit < PACKAGE_RESTAURANT_MIN_RON
      ? PACKAGE_RESTAURANT_MIN_RON
      : parsedCredit;
    setRestaurantCreditInput(String(clamped));
    return clamped;
  };

  const selectedRoom = useMemo(
    () => roomTypes.find((r) => r.id === roomTypeId) || roomTypes[0] || null,
    [roomTypes, roomTypeId],
  );

  const nightlyRon = selectedRoom ? (withSpa ? selectedRoom.withSpaPriceRon : selectedRoom.basePriceRon) : 0;
  const accommodationRon = nightlyRon * nights;
  const servicesRon = PACKAGE_SPA_SERVICES.reduce(
    (sum, svc) => sum + (serviceCounts[svc.key] || 0) * svc.unitPriceRon,
    0,
  );
  const sparklingRon = sparklingWine ? PACKAGE_SPARKLING_WINE_PRICE_RON : 0;
  const totalRon = accommodationRon + restaurantCredit + servicesRon + sparklingRon;

  const updateServiceCount = (key: string, delta: number) => {
    setServiceCounts((prev) => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  };

  const handleContinue = () => {
    if (!selectedRoom) return;
    const restaurantCreditRon = restaurantCreditOn ? clampRestaurantCredit() : 0;
    setDraft({
      type: 'package',
      roomTypeId: selectedRoom.id,
      roomTypeName: selectedRoom.displayName,
      nights,
      withSpa,
      sparklingWine,
      sparklingNote,
      restaurantCreditRon,
      spaServices: PACKAGE_SPA_SERVICES.map((svc) => ({
        key: svc.key,
        count: serviceCounts[svc.key] || 0,
        unitPriceRon: svc.unitPriceRon,
      })),
      basePriceRon: selectedRoom.basePriceRon,
      withSpaPriceRon: selectedRoom.withSpaPriceRon,
    });
    router.push('/gift-vouchers/checkout');
  };

  if (roomTypes.length === 0) {
    return (
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 text-amber-800 text-sm">
        No room types are currently available for the curated voucher. An admin can enable rooms in the room-types CMS.
      </div>
    );
  }

  return (
    <>
    <MobileStickyTotal totalRon={totalRon} />
    <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
      <div className="lg:col-span-3 space-y-6">
        {/* Room type */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-poppins text-lg font-semibold text-[#2c1810] mb-4">{t('curated.step1')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {roomTypes.map((rt) => (
              <button
                key={rt.id}
                type="button"
                onClick={() => setRoomTypeId(rt.id)}
                className={`px-5 py-4 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer ${
                  roomTypeId === rt.id
                    ? 'border-[#801515] bg-[#801515]/5 shadow-md'
                    : 'border-gray-200 hover:border-[#801515]/30 bg-white'
                }`}
              >
                <span className={`font-medium ${roomTypeId === rt.id ? 'text-[#801515]' : 'text-[#2c1810]'}`}>
                  {rt.displayName}
                </span>
                <span className="block text-xs text-gray-400 mt-0.5">
                  {t('curated.fromPerNight', { price: rt.basePriceRon.toLocaleString() })}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Nights */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-poppins text-lg font-semibold text-[#2c1810] mb-4">{t('curated.step2')}</h3>
          <label className="block text-sm font-medium text-[#2c1810] mb-3">{t('curated.numberOfNights')}</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setNights(Math.max(1, nights - 1))}
              className="w-10 h-10 rounded-xl bg-[#faf8f6] border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="text-2xl font-bold text-[#2c1810] w-12 text-center">{nights}</span>
            <button
              type="button"
              onClick={() => setNights(nights + 1)}
              className="w-10 h-10 rounded-xl bg-[#faf8f6] border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Add-ons */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-poppins text-lg font-semibold text-[#2c1810] mb-4">{t('curated.addOns')}</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#478B8B]/40 transition-all cursor-pointer has-[:checked]:border-[#478B8B] has-[:checked]:bg-[#478B8B]/5">
              <input
                type="checkbox"
                checked={withSpa}
                onChange={() => setWithSpa(!withSpa)}
                className="mt-1 w-5 h-5 rounded accent-[#478B8B]"
              />
              <span className="font-medium text-[#2c1810] flex-1">{t('curated.spaAccess')}</span>
            </label>
            <label className="flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#daa520]/40 transition-all cursor-pointer has-[:checked]:border-[#daa520] has-[:checked]:bg-[#daa520]/5">
              <input
                type="checkbox"
                checked={sparklingWine}
                onChange={() => setSparklingWine(!sparklingWine)}
                className="mt-1 w-5 h-5 rounded accent-[#daa520]"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-[#2c1810] flex items-center gap-2">
                    <Wine className="w-4 h-4 text-[#daa520] shrink-0" />
                    {t('curated.sparklingWine')}
                  </span>
                  <span className="text-sm font-semibold text-[#daa520] whitespace-nowrap">+{PACKAGE_SPARKLING_WINE_PRICE_RON} RON</span>
                </div>
                {sparklingWine && (
                  <input
                    type="text"
                    placeholder={t('curated.sparklingNote')}
                    value={sparklingNote}
                    onChange={(e) => setSparklingNote(e.target.value)}
                    className="mt-3 w-full px-4 py-2.5 bg-[#faf8f6] border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#daa520]/30 focus:border-[#daa520] outline-none transition-all"
                  />
                )}
              </div>
            </label>
            <label className="flex items-start gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#801515]/40 transition-all cursor-pointer has-[:checked]:border-[#801515] has-[:checked]:bg-[#801515]/5">
              <input
                type="checkbox"
                checked={restaurantCreditOn}
                onChange={toggleRestaurantCredit}
                className="mt-1 w-5 h-5 rounded accent-[#801515]"
              />
              <div className="flex-1">
                <span className="font-medium text-[#2c1810]">{t('curated.restaurantCredit')}</span>
                {restaurantCreditOn && (
                  <div className="mt-3">
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={PACKAGE_RESTAURANT_MIN_RON}
                        step={50}
                        value={restaurantCreditInput}
                        onChange={(e) => setRestaurantCreditInput(e.target.value)}
                        onBlur={clampRestaurantCredit}
                        className="w-full px-4 py-2.5 bg-[#faf8f6] border border-gray-200 rounded-xl text-lg font-semibold text-[#2c1810] focus:ring-2 focus:ring-[#801515]/30 focus:border-[#801515] outline-none transition-all"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">RON</span>
                    </div>
                    <p className={`text-xs mt-1 ${restaurantCreditBelowMin ? 'text-[#801515]' : 'text-gray-400'}`}>{t('curated.minRestaurant')}</p>
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Optional services */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-poppins text-lg font-semibold text-[#2c1810] mb-4">{t('curated.step3')}</h3>
          <div className="space-y-3">
            {PACKAGE_SPA_SERVICES.map((svc) => {
              const count = serviceCounts[svc.key] || 0;
              return (
                <div
                  key={svc.key}
                  className={`flex items-center justify-between gap-3 p-4 rounded-2xl border-2 transition-all ${
                    count > 0 ? 'border-[#478B8B] bg-[#478B8B]/5' : 'border-gray-200'
                  }`}
                >
                  <div className="min-w-0">
                    <span className="font-medium text-[#2c1810] break-words">{t(`curated.services.${svc.key}`)}</span>
                    <span className="block text-xs text-[#478B8B] mt-0.5">{svc.unitPriceRon} RON / {t('curated.servicesEach')}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => updateServiceCount(svc.key, -1)}
                      className="w-8 h-8 rounded-lg bg-[#faf8f6] border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-lg font-bold text-[#2c1810] w-8 text-center">{count}</span>
                    <button
                      type="button"
                      onClick={() => updateServiceCount(svc.key, 1)}
                      className="w-8 h-8 rounded-lg bg-[#faf8f6] border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="lg:col-span-2">
        <div className="lg:sticky lg:top-24">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-5 sm:p-6">
            <h3 className="font-poppins text-lg font-semibold text-[#2c1810] mb-4">{t('curated.summary')}</h3>
            <div className="space-y-3 text-sm">
              {selectedRoom && (
                <>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500 min-w-0 break-words">{selectedRoom.displayName}</span>
                    <span className="text-[#2c1810] shrink-0">{nights} × {nightlyRon.toLocaleString()} RON</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500 min-w-0 break-words">{t('curated.summaryAccommodation')}</span>
                    <span className="font-medium text-[#2c1810] shrink-0">{accommodationRon.toLocaleString()} RON</span>
                  </div>
                </>
              )}
              {withSpa && (
                <div className="flex justify-between gap-2 text-[#478B8B]">
                  <span className="min-w-0 break-words">{t('curated.spaAccess')}</span>
                  <span className="shrink-0"><Check className="w-4 h-4 inline" /></span>
                </div>
              )}
              {sparklingWine && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 min-w-0 break-words">{t('curated.sparklingWine')}</span>
                  <span className="text-[#2c1810] shrink-0">+{PACKAGE_SPARKLING_WINE_PRICE_RON} RON</span>
                </div>
              )}
              {restaurantCredit > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 min-w-0 break-words">{t('curated.restaurantCredit')}</span>
                  <span className="text-[#2c1810] shrink-0">{restaurantCredit.toLocaleString()} RON</span>
                </div>
              )}
              {PACKAGE_SPA_SERVICES.filter((svc) => (serviceCounts[svc.key] || 0) > 0).map((svc) => (
                <div key={svc.key} className="flex justify-between gap-2">
                  <span className="text-gray-500 min-w-0 break-words mr-2">
                    {t(`curated.services.${svc.key}`)} x{serviceCounts[svc.key]}
                  </span>
                  <span className="text-[#2c1810] whitespace-nowrap shrink-0">
                    +{(svc.unitPriceRon * serviceCounts[svc.key]).toLocaleString()} RON
                  </span>
                </div>
              ))}

              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-[#2c1810]">{t('total')}</span>
                  <span className="text-2xl font-bold text-[#801515]">{totalRon.toLocaleString()} RON</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleContinue}
              className="w-full mt-6 py-4 rounded-2xl bg-[#801515] text-white font-semibold text-lg hover:bg-[#601010] active:scale-[0.98] transition-all duration-200 shadow-lg shadow-[#801515]/25 cursor-pointer"
            >
              {t('proceedToPayment')}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
